import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { updateSettings } from '@/store/tournamentSlice';
import { TournamentSettings as TournamentSettingsType, GameType } from '@/types';
import clsx from 'clsx';
import { Settings, Save, RotateCcw } from 'lucide-react';

export const TournamentSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tournament = useSelector((state: RootState) => state.tournament.current);
  
  const [formData, setFormData] = useState<TournamentSettingsType>(
    tournament?.settings || {} as TournamentSettingsType
  );
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: keyof TournamentSettingsType, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    dispatch(updateSettings(formData));
    setHasChanges(false);
  };

  const handleReset = () => {
    if (tournament?.settings) {
      setFormData(tournament.settings);
      setHasChanges(false);
    }
  };

  const gameTypeOptions = [
    { value: GameType.LIMIT, label: 'Limit' },
    { value: GameType.POT_LIMIT, label: 'Pot Limit' },
    { value: GameType.NO_LIMIT, label: 'No Limit' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings size={24} />
              Tournament Settings
            </h2>
            
            <div className="flex gap-2">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  <RotateCcw size={16} />
                  Reset
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                  {
                    'bg-green-600 hover:bg-green-700 text-white': hasChanges,
                    'bg-gray-300 text-gray-500 cursor-not-allowed': !hasChanges,
                  }
                )}
              >
                <Save size={16} />
                Save Changes
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Basic Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tournament Name
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter tournament name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Game Type
                </label>
                <select
                  value={formData.gameType || GameType.NO_LIMIT}
                  onChange={(e) => handleChange('gameType', e.target.value as GameType)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {gameTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Tournament notes..."
              />
            </div>
          </div>

          {/* Entry Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Entry Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Fee
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">
                    {formData.currencySymbol || '$'}
                  </span>
                  <input
                    type="number"
                    value={formData.entryFee || ''}
                    onChange={(e) => handleChange('entryFee', parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Starting Chips
                </label>
                <input
                  type="number"
                  value={formData.startingChips || ''}
                  onChange={(e) => handleChange('startingChips', parseInt(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Players per Table
                </label>
                <input
                  type="number"
                  value={formData.maxPlayersPerTable || ''}
                  onChange={(e) => handleChange('maxPlayersPerTable', parseInt(e.target.value) || 8)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="2"
                  max="10"
                />
              </div>
            </div>
          </div>

          {/* Rake Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rake Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fixed Rake
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">
                    {formData.currencySymbol || '$'}
                  </span>
                  <input
                    type="number"
                    value={formData.fixedRake || ''}
                    onChange={(e) => handleChange('fixedRake', parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Per-Player Rake
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">
                    {formData.currencySymbol || '$'}
                  </span>
                  <input
                    type="number"
                    value={formData.perPlayerRake || ''}
                    onChange={(e) => handleChange('perPlayerRake', parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency Symbol
                </label>
                <input
                  type="text"
                  value={formData.currencySymbol || ''}
                  onChange={(e) => handleChange('currencySymbol', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={3}
                  placeholder="$"
                />
              </div>
            </div>
          </div>

          {/* Rebuy Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rebuy Settings</h3>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.allowRebuys || false}
                  onChange={(e) => handleChange('allowRebuys', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Allow Rebuys</span>
              </label>
            </div>
            
            {formData.allowRebuys && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rebuy Fee
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">
                      {formData.currencySymbol || '$'}
                    </span>
                    <input
                      type="number"
                      value={formData.rebuyFee || ''}
                      onChange={(e) => handleChange('rebuyFee', parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rebuy Chips
                  </label>
                  <input
                    type="number"
                    value={formData.rebuyChips || ''}
                    onChange={(e) => handleChange('rebuyChips', parseInt(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Disable After Round
                  </label>
                  <input
                    type="number"
                    value={formData.disableRebuysAfterRound || ''}
                    onChange={(e) => handleChange('disableRebuysAfterRound', parseInt(e.target.value) || 9999)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="1"
                  />
                </div>
                
                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.applyRakeToRebuys || false}
                      onChange={(e) => handleChange('applyRakeToRebuys', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Apply Rake to Rebuys</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Add-on Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add-on Settings</h3>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.allowAddOns || false}
                  onChange={(e) => handleChange('allowAddOns', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Allow Add-ons</span>
              </label>
            </div>
            
            {formData.allowAddOns && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add-on Fee
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">
                      {formData.currencySymbol || '$'}
                    </span>
                    <input
                      type="number"
                      value={formData.addOnFee || ''}
                      onChange={(e) => handleChange('addOnFee', parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add-on Chips
                  </label>
                  <input
                    type="number"
                    value={formData.addOnChips || ''}
                    onChange={(e) => handleChange('addOnChips', parseInt(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Disable After Round
                  </label>
                  <input
                    type="number"
                    value={formData.disableAddOnsAfterRound || ''}
                    onChange={(e) => handleChange('disableAddOnsAfterRound', parseInt(e.target.value) || 9999)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="1"
                  />
                </div>
                
                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.applyRakeToAddOns || false}
                      onChange={(e) => handleChange('applyRakeToAddOns', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Apply Rake to Add-ons</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Timer Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timer Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hand Timer Default (seconds)
                </label>
                <input
                  type="number"
                  value={formData.handTimerDefaultSeconds || ''}
                  onChange={(e) => handleChange('handTimerDefaultSeconds', parseInt(e.target.value) || 60)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="10"
                  max="300"
                />
              </div>
              
              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.pauseBetweenRounds || false}
                    onChange={(e) => handleChange('pauseBetweenRounds', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Pause Between Rounds</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};